import type { Sort } from '@/stores/sorting';
import type { PaginatedUsersResponse, UserFilter, UserRequest, UserResponse } from '@/types';
import type { UserTwoFactorRecoveryCodesResponse, UserTwoFactorSetupResponse } from '@/types/user.ts';
import { sortToQuery } from '@/utils/misc.ts';
import type { AxiosInstance, AxiosResponse } from 'axios';

class UserService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    getPath(id?: string) {
        return id ? `/api/users/${id}` : '/api/users';
    }

    index(page: number, filter?: UserFilter, sort: null | Sort = null, perPage?: number) {
        return this.apiService.get(this.getPath(), {
            params: { page: page, per_page: perPage, ...filter, ...sortToQuery(sort) },
        });
    }

    /** Typeahead search across name/email, returning a small set of active users. */
    searchByName(query: string, perPage = 20): Promise<AxiosResponse<PaginatedUsersResponse>> {
        return this.apiService.get(this.getPath(), {
            params: { search: query, inactive_at: 'active', per_page: perPage },
        });
    }

    show(id: string) {
        return this.apiService.get(this.getPath(id));
    }

    store(request: UserRequest) {
        return this.apiService.post(this.getPath(), request);
    }

    update(id: string, request: UserRequest): Promise<AxiosResponse<UserResponse>> {
        return this.apiService.put(this.getPath(id), request);
    }

    delete(id: string) {
        return this.apiService.delete(this.getPath(id));
    }

    me(): Promise<AxiosResponse<UserResponse>> {
        return this.apiService.get('/api/user');
    }

    deleteAccount(password: string): Promise<AxiosResponse> {
        return this.apiService.delete('/api/account', {
            data: { password, confirmation: true },
        });
    }

    deactivate(id: string): Promise<AxiosResponse<UserResponse>> {
        return this.apiService.put(this.getPath(id), {
            inactive_at: new Date().toISOString(),
        });
    }

    reactivate(id: string): Promise<AxiosResponse<UserResponse>> {
        return this.apiService.put(this.getPath(id), {
            inactive_at: null,
        });
    }

    loginAs(id: string, reason: string): Promise<AxiosResponse<UserResponse>> {
        return this.apiService.post(`/api/admin/impersonate/${id}`, { reason });
    }

    logoutAs(): Promise<AxiosResponse> {
        return this.apiService.post(`/api/admin/impersonate/stop`);
    }

    setupTwoFactor(): Promise<AxiosResponse<UserTwoFactorSetupResponse>> {
        return this.apiService.get(`/api/user/2fa/setup`);
    }

    activateTwoFactor(code: string): Promise<AxiosResponse<UserTwoFactorRecoveryCodesResponse>> {
        return this.apiService.post(`/api/user/2fa/activate`, { code });
    }

    deactivateTwoFactor(code: string): Promise<AxiosResponse> {
        return this.apiService.delete(`/api/user/2fa/deactivate`, { data: { code } });
    }
}

export default UserService;
